import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SubmissionsTable = ({ submissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredSubmissions = submissions?.filter(submission =>
    submission?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    submission?.source?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubmissions?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubmissions = filteredSubmissions?.slice(startIndex, startIndex + itemsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'abandoned':
        return 'bg-destructive/10 text-destructive';
      case 'in-progress':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Timestamp', 'Email', 'Source', 'Device', 'Status', 'Completion Time'],
      ...filteredSubmissions?.map(s => [
        s?.timestamp,
        s?.email,
        s?.source,
        s?.device,
        s?.status,
        s?.completionTime
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-submissions-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  return (
    <div className="bg-card border border-border rounded-md shadow-elevation-sm">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Recent Submissions
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="w-full sm:w-64">
              <Input
                type="search"
                placeholder="Search by email or source..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e?.target?.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Device
              </th>
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Time
              </th>
              <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedSubmissions?.map((submission) => (
              <tr key={submission?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {submission?.timestamp}
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {submission?.email}
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {submission?.source}
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-muted-foreground capitalize">
                  {submission?.device}
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusColor(submission?.status)}`}>
                    {submission?.status}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {submission?.completionTime}
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right text-sm">
                  <button className="text-primary hover:text-primary/80 transition-smooth">
                    <Icon name="Eye" size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="p-4 md:p-6 border-t border-border flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredSubmissions?.length)} of {filteredSubmissions?.length} results
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            />
            <span className="text-sm text-foreground px-3">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsTable;